
export interface RingSegmentProps {
    radiusInner?: number,
    radiusOuter?: number,
    /** 0 = top, 0.25 = right, 0.5 = bottom, 0.75 = left, 1.0 = top again, ... */
    degreeStart: number,
    /** 0 = top, 0.25 = right, 0.5 = bottom, 0.75 = left, 1.0 = top again, ...
     * should be between degreeStart and degreeStart + 1
     * (if it's less, nothing is displayed, if it's more, a full circle is displayed)
     */
    degreeEnd: number,
    color: string,
    className?: string
}

/**
 * This RingSegment component creates a ring segment with inner and outer radius, leaving the inner area free. 
 * You can use the radiusInner, radiusOuter, degreeStart, degreeEnd and color props to display the desired ring segment.
 * @returns 
 */
export const RingSegment = ({ radiusInner = 10, radiusOuter = 58, degreeStart, degreeEnd, color, className }: RingSegmentProps) => {
    // console.debug('🪐RingSegment', { radiusInner, radiusOuter, degreeStart, degreeEnd, color, className })

    // degreeStart may be any value (not necessarily between 0 and 1).
    // degreeEnd must be in [degreeStart, degreeStart + 1] for an actual ring segment to be displayed.
    if (degreeEnd < degreeStart) {
        // if it's smaller than degreeStart, nothing will be displayed.
        return <div className="ring-segment"></div>
    }
    if (degreeEnd > degreeStart + 1) {
        // if it's larger than degreeStart + 1, a full circle will be displayed.
        degreeEnd = degreeStart + 1;
    }

    // Umrechnen der Grad in Bogenmaß
    const startAngle = degreeStart * 2 * Math.PI;
    const endAngle = degreeEnd * 2 * Math.PI;

    // Berechnen Sie die Start- und Endpunkte des Ringsegments
    const startXInner = radiusInner * Math.cos(startAngle);
    const startYInner = radiusInner * Math.sin(startAngle);
    const endXInner = radiusInner * Math.cos(endAngle);
    const endYInner = radiusInner * Math.sin(endAngle);

    const startXOuter = radiusOuter * Math.cos(startAngle);
    const startYOuter = radiusOuter * Math.sin(startAngle);
    const endXOuter = radiusOuter * Math.cos(endAngle);
    const endYOuter = radiusOuter * Math.sin(endAngle);

    // Berechnen Sie die Verschiebung, um das Segment zentriert zu positionieren
    const translateX = radiusOuter;
    const translateY = radiusOuter;

    // Bestimmen Sie den large-arc-flag-Wert basierend auf dem Gradbereich
    const largeArcFlag = (degreeEnd - degreeStart) > 0.5 ? 1 : 0;
    const outerSweepFlag = (degreeEnd - degreeStart) > 0 ? 1 : 0;
    const innerSweepFlag = 1 - outerSweepFlag;

    // Erstellen Sie den d-Schritt für das <path>-Element
    const pathData = `M ${startXOuter},${startYOuter} 
                    A ${radiusOuter},${radiusOuter} 0 ${largeArcFlag} ${outerSweepFlag} ${endXOuter},${endYOuter} 
                    L ${endXInner},${endYInner} 
                    A ${radiusInner},${radiusInner} 0 ${largeArcFlag} ${innerSweepFlag} ${startXInner},${startYInner} 
                    Z`;

    return (
        <div className={"ring-segment "+(className || "")}>
            <svg width={2 * radiusOuter} height={2 * radiusOuter}>
                <g transform={`translate(${translateX},${translateY}) rotate(-90)`}>
                    <path d={pathData} fill={color} />
                </g>
            </svg>
        </div>
    );
};

